import { useEffect, useState } from "react";
import { openingStatus, parseOpeningHours, type OpeningHours, type OpeningState } from "@majourneys/shared";

/**
 * The current moment, read in the browser after mount and refreshed every
 * minute. Null during the server render and the first client render, so
 * anything derived from the clock (open now, closing soon) never makes the
 * hydrated HTML differ from the server's: the site has had React #418 from
 * host-clock rendering. Opening state is then judged on Morocco's wall clock
 * by `openingStatus`, whatever the visitor's own zone.
 */
export function useClientNow(intervalMs = 60_000, enabled = true): Date | null {
  const [now, setNow] = useState<Date | null>(null);
  useEffect(() => {
    // Off: no clock and no minute tick re-rendering the caller for nothing.
    if (!enabled) return;
    setNow(new Date());
    const id = window.setInterval(() => setNow(new Date()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs, enabled]);
  return enabled ? now : null;
}

/** Open / closing soon / closed at `now`, or null when unknown or before mount. */
export function openStateAt(hours: unknown, now: Date | null): OpeningState | null {
  if (!now || !parseOpeningHours(hours)) return null;
  return openingStatus(hours as OpeningHours, now)?.state ?? null;
}
